import { apiAiChatCompletions, apiAiChatCompletionsStream } from '../../../../api/ai';
import {
  buildTypedContextPayload,
  buildOpenRouterMessages,
  serializeOpenRouterChatRequest,
  extractAssistantTextFromOpenRouter
} from '../../aiResponseHelpers';
import { getAiModelById } from '../../aiConfigRuntimeHelpers';
import { createStageResult } from '../contracts';
import {
  createRequestAttemptSuccessArtifacts,
  createRequestAttemptFailureArtifacts
} from './logging';

const OPENROUTER_RATE_LIMIT_COOLDOWN_MS = 45000;
const createAttemptKey = (requestKey = null, attemptIndex = 1) => {
  const prefix = String(requestKey || '').trim();
  const suffix = Math.max(1, Number(attemptIndex || 1));
  return prefix ? `${prefix}_${suffix}` : suffix;
};

// Creates the initial request-stage result payload for candidate model execution.
export const createRequestStartStageResult = ({
  candidateModels = [],
  candidateModel = null,
  stream = false,
  usedLegacyRequest = false,
  routingMode = 'hybrid'
} = {}) => {
  return createStageResult({
    stage: 'request',
    status: 'ok',
    decisionCode: 'request_candidates_built',
    payload: {
      candidateCount: Array.isArray(candidateModels) ? candidateModels.length : 0,
      candidateModels: Array.isArray(candidateModels) ? candidateModels : [],
      candidateModel: candidateModel || null,
      stream: Boolean(stream),
      usedLegacyRequest: Boolean(usedLegacyRequest),
      routingMode: String(routingMode || 'hybrid').trim().toLowerCase() || 'hybrid'
    }
  });
};

// Creates a mutable candidate queue and ensures worker-default fallback is available.
export const createRequestCandidateQueue = ({
  candidates = []
} = {}) => {
  const queue = Array.isArray(candidates) ? [...candidates] : [];
  if (!queue.includes('')) {
    queue.push('');
  }
  return queue;
};

// Builds request candidate model list with fallback models and unavailable-model filtering.
export const createRequestCandidates = ({
  model = null,
  fallbackModels = [],
  unavailableModelIds
} = {}) => {
  const candidates = Array.from(new Set([
    model,
    ...(Array.isArray(fallbackModels) ? fallbackModels : [])
  ].map(value => String(value || '').trim()).filter(Boolean)))
    .filter(modelId => !(unavailableModelIds && typeof unavailableModelIds.has === 'function' && unavailableModelIds.has(modelId)));

  if (candidates.length === 0) {
    candidates.push('');
  }

  return candidates;
};

// Captures a clipped user-request excerpt for request-attempt logging.
export const captureRequestExcerpt = ({
  messages = [],
  shouldCapturePayloads = false,
  maxExcerptChars = 700,
  clipText
} = {}) => {
  if (!shouldCapturePayloads || typeof clipText !== 'function') return '';
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (String(message?.role || '').toLowerCase() !== 'user') continue;
    return clipText(String(message?.content || ''), maxExcerptChars);
  }
  return '';
};

// Resolves total USD cost from usage through a pluggable cost calculator.
export const resolveCostUsdValue = ({
  modelId = null,
  usage = null,
  calculateUsageCost
} = {}) => {
  if (typeof calculateUsageCost !== 'function') return 0;
  const resolved = calculateUsageCost({ modelId, usage });
  const value = Number(resolved?.totalCostUsd || 0);
  return Number.isFinite(value) ? value : 0;
};

const toSafeInteger = (value, fallback = 0) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, Math.round(numeric));
};

export const extractUsageMetrics = (response = null) => {
  const usage = response?.usage && typeof response.usage === 'object' ? response.usage : {};
  const promptTokens = toSafeInteger(
    usage.prompt_tokens
    ?? usage.input_tokens
    ?? usage.promptTokens
    ?? usage.inputTokens
  );
  const completionTokens = toSafeInteger(
    usage.completion_tokens
    ?? usage.output_tokens
    ?? usage.completionTokens
    ?? usage.outputTokens
  );
  const totalTokens = toSafeInteger(
    usage.total_tokens
    ?? usage.totalTokens
    ?? (promptTokens + completionTokens)
  );

  if (promptTokens <= 0 && completionTokens <= 0 && totalTokens <= 0) {
    return null;
  }

  return {
    promptTokens,
    completionTokens,
    totalTokens
  };
};

export const calculateUsageCostUsd = ({ modelId, usage }) => {
  if (!usage || !modelId) return null;
  const model = getAiModelById(modelId);
  const promptRate = Number(model?.pricing?.promptPer1M || 0);
  const completionRate = Number(model?.pricing?.completionPer1M || 0);
  if (!Number.isFinite(promptRate) || !Number.isFinite(completionRate)) return null;

  const promptCost = (Number(usage.promptTokens || 0) / 1000000) * promptRate;
  const completionCost = (Number(usage.completionTokens || 0) / 1000000) * completionRate;
  const totalCost = Number((promptCost + completionCost).toFixed(6));

  return {
    promptCostUsd: Number(promptCost.toFixed(6)),
    completionCostUsd: Number(completionCost.toFixed(6)),
    totalCostUsd: totalCost,
    currency: String(model?.pricing?.currency || 'USD').trim() || 'USD',
    modelTier: model?.tier || null
  };
};

export const sleep = (ms = 0) => new Promise((resolve) => setTimeout(resolve, Math.max(0, Number(ms || 0))));

export const buildRateLimitError = (remainingMs = OPENROUTER_RATE_LIMIT_COOLDOWN_MS) => {
  const seconds = Math.max(1, Math.ceil(Number(remainingMs || 0) / 1000));
  const error = new Error(`OpenRouter rate limit is active. Retry in about ${seconds}s.`);
  error.status = 429;
  error.code = 'OPENROUTER_RATE_LIMITED';
  error.retryAfterMs = Math.max(0, Number(remainingMs || 0));
  return error;
};

// Detects provider policy/guardrail restriction failures.
export const isGuardrailRestrictionError = (error) => {
  const message = String(error?.message || '');
  return /guardrail restrictions|data policy|privacy/i.test(message);
};

// Determines whether a request error should trigger candidate-level retry logic.
export const shouldRetryRequestError = (error) => {
  const status = Number(error?.status || 0);
  if (status === 429) return true;
  if (status === 404 || status === 503) return true;
  const message = String(error?.message || '');
  return /429|too many requests|rate limit|no endpoints|no endpoints available|guardrail restrictions|data policy|provider returned error/i.test(message);
};

// Identifies model-unavailable failures that should mark a candidate as unavailable.
export const isModelUnavailableError = (error) => {
  if (isGuardrailRestrictionError(error)) return false;
  const status = Number(error?.status || 0);
  const message = String(error?.message || '');
  if (/provider returned error/i.test(message)) return false;
  if (status === 404 && /model not found|unknown model|no endpoints/i.test(message)) return true;
  return /no endpoints|model not found|not found|unknown model/i.test(message);
};

// Detects transient provider/rate-limit errors suitable for short backoff retries.
export const isTransientRateLimitError = (error) => {
  const status = Number(error?.status || 0);
  if (status === 429) return true;
  const message = String(error?.message || '');
  return /too many requests|rate limit|provider returned error|temporarily unavailable|overloaded/i.test(message);
};

// Builds selected request messages and typed payload metadata for the response stage.
export const executeSelectStage = ({
  docJson,
  userText = '',
  systemPrompt,
  maxHistoryMessages = 20,
  orchestrationEnabled = false,
  intentKey = null,
  chatMode = 'Explore',
  formatPolicy = null,
  contextItems = [],
  selectionContexts = [],
  goalState = null
} = {}) => {
  const typedPayload = orchestrationEnabled
    ? buildTypedContextPayload({ userText, contextItems, intentKey, chatMode })
    : null;

  const messages = buildOpenRouterMessages({
    docJson,
    userText,
    systemPrompt,
    maxHistoryMessages,
    orchestrationEnabled,
    intentKey,
    chatMode,
    formatPolicy,
    contextItems,
    selectionContexts,
    goalState
  });

  return {
    typedPayload,
    messages,
    selectResult: createStageResult({
      stage: 'select',
      status: 'ok',
      decisionCode: 'select_messages_built',
      payload: {
        messageCount: Array.isArray(messages) ? messages.length : 0,
        contextCount: Array.isArray(contextItems) ? contextItems.length : 0,
        contextChars: (Array.isArray(contextItems) ? contextItems : []).reduce((total, item) => total + String(item?.text || '').length, 0)
      }
    })
  };
};

// Builds legacy OpenRouter messages when orchestration selection/request falls back.
export const buildLegacyMessages = ({ docJson, userText, systemPrompt, maxHistoryMessages }) => {
  return buildOpenRouterMessages({
    docJson,
    userText,
    systemPrompt,
    maxHistoryMessages,
    orchestrationEnabled: false,
    intentKey: null,
    formatPolicy: null,
    contextItems: []
  });
};

// Alias with behavior-oriented naming; legacy symbol remains for compatibility.
export const buildFallbackMessages = buildLegacyMessages;

// Executes a single request attempt through stream or non-stream transport paths.
export const executeRequestStage = async ({
  messages = [],
  model,
  temperature,
  maxTokens,
  stream = false,
  signal,
  onDelta
} = {}) => {
  const serialized = serializeOpenRouterChatRequest({
    model,
    messages,
    temperature,
    maxTokens,
    stream
  });

  if (stream) {
    const streamed = await apiAiChatCompletionsStream({
      ...serialized,
      signal,
      onDelta
    });

    return {
      responseText: String(streamed?.text || ''),
      responseRaw: streamed?.raw || null,
      requestResult: createStageResult({
        stage: 'request',
        status: 'ok',
        decisionCode: 'request_stream_started',
        payload: {
          candidateModel: model || null,
          transport: 'stream'
        }
      }),
      streamResult: createStageResult({
        stage: 'stream',
        status: 'ok',
        decisionCode: 'stream_complete',
        payload: {
          candidateModel: model || null,
          responseModel: streamed?.raw?.model || model || null,
          transport: 'stream'
        }
      })
    };
  }

  const response = await apiAiChatCompletions({
    ...serialized,
    signal
  });

  return {
    responseText: extractAssistantTextFromOpenRouter(response),
    responseRaw: response,
    requestResult: createStageResult({
      stage: 'request',
      status: 'ok',
      decisionCode: 'request_single_complete',
      payload: {
        candidateModel: model || null,
        responseModel: response?.model || model || null,
        transport: 'single'
      }
    }),
    streamResult: createStageResult({
      stage: 'stream',
      status: 'ok',
      decisionCode: 'stream_single_response',
      payload: {
        candidateModel: model || null,
        responseModel: response?.model || model || null,
        transport: 'single'
      }
    })
  };
};

// Runs candidate-model request attempts with retry/fallback behavior and telemetry hooks.
export const executeRequestWithCandidateRetries = async ({
  requestMessages = [],
  candidates = [],
  stream = false,
  signal,
  temperature,
  maxTokens,
  usedLegacyRequest = false,
  routingMode = 'hybrid',
  requestId = null,
  onStreamText,
  normalizeStreamText,
  emitStage,
  requestStageEmitted = false,
  shouldCapturePayloads = false,
  maxExcerptChars = 700,
  clipText,
  calculateUsageCost,
  extractUsage,
  extractResponseText,
  callKind = 'response',
  chatDocId = null,
  activeProjectId = null,
  billingOwnerUserId = null,
  initiatorUserId = null,
  logAttemptsEnabled = false,
  requestLogReady = false,
  insertRequestAttempt,
  requestAttemptCounterStart = 0,
  blockedModelSet,
  normalizeResponseModelId,
  isBlockedResponseModel,
  unavailableModelIds,
  isRateLimitedStatus,
  isTransientRateLimitError,
  isModelUnavailableError,
  isGuardrailRestrictionError,
  shouldRetryRequestError,
  setRateLimitUntil,
  rateLimitCooldownMs = 45000,
  buildRateLimitError: buildRateLimitErrorFn,
  maxTransientRetriesPerCandidate = 2,
  baseTransientRetryDelayMs = 800,
  sleep: sleepFn,
  onTraceWarn,
  onWarn,
  debugLabel = 'request',
  executeRequestStageFn = executeRequestStage
} = {}) => {
  const buildExecutionState = () => ({
    response,
    responseText,
    requestAttemptsAudit: Array.isArray(requestAttemptsAudit) ? [...requestAttemptsAudit] : [],
    requestAttemptCounter,
    requestStageEmitted
  });

  let lastError = null;
  let response = null;
  let responseText = '';
  let streamStageEmitted = false;
  const candidateQueue = createRequestCandidateQueue({ candidates });
  let workerDefaultQueued = false;
  const transientRetriesByCandidate = new Map();
  const requestAttemptsAudit = [];
  let requestAttemptCounter = Number(requestAttemptCounterStart || 0);

  const requestExcerpt = captureRequestExcerpt({
    messages: requestMessages,
    shouldCapturePayloads,
    maxExcerptChars,
    clipText
  });

  const handleStreamDelta = (candidateModel) => (nextText, delta, payload) => {
    if (!streamStageEmitted) {
      streamStageEmitted = true;
      if (typeof emitStage === 'function') {
        emitStage('stream', {
          stream: true,
          candidateModel: candidateModel || null,
          responseModel: payload?.model || null,
          usedLegacyRequest,
          transport: 'stream'
        });
      }
    }
    responseText = nextText;
    if (typeof onStreamText === 'function') {
      onStreamText(typeof normalizeStreamText === 'function' ? normalizeStreamText(nextText) : nextText, {
        delta,
        payload,
        model: candidateModel,
        requestId
      });
    }
  };

  for (let index = 0; index < candidateQueue.length; index += 1) {
    const candidateModel = candidateQueue[index] || undefined;
    const retryKey = candidateModel || '__worker_default__';
    const attemptIndex = requestAttemptCounter + 1;
    const attemptKey = createAttemptKey(requestId, attemptIndex);
    requestAttemptCounter = attemptIndex;
    const attemptStart = Date.now();

    if (index === 0 && !requestStageEmitted) {
      requestStageEmitted = true;
      const requestStartResult = createRequestStartStageResult({
        candidateModels: candidateQueue,
        candidateModel: candidateModel || null,
        stream,
        usedLegacyRequest,
        routingMode
      });
      if (typeof emitStage === 'function') {
        emitStage('request', {
          status: requestStartResult?.status || 'ok',
          decisionCode: requestStartResult?.decisionCode || 'request_candidates_built',
          ...(requestStartResult?.payload || {})
        });
      }
    }

    try {
      const requestExecution = await executeRequestStageFn({
        messages: requestMessages,
        model: candidateModel,
        temperature,
        maxTokens,
        stream,
        signal,
        onDelta: stream ? handleStreamDelta(candidateModel) : undefined
      });

      response = requestExecution?.responseRaw || null;
      responseText = requestExecution?.responseText || responseText;

      const successUsage = typeof extractUsage === 'function'
        ? extractUsage(response)
        : { promptTokens: 0, completionTokens: 0, totalTokens: 0 };
      const successModel = String(response?.model || candidateModel || '').trim() || null;
      const successCost = typeof calculateUsageCost === 'function'
        ? (calculateUsageCost({ modelId: successModel, usage: successUsage }) || { totalCostUsd: 0 })
        : { totalCostUsd: 0 };
      const providerRequestId = String(response?.id || '').trim() || null;

      const successArtifacts = createRequestAttemptSuccessArtifacts({
        attemptIndex,
        attemptKey,
        usedLegacyRequest,
        candidateModel,
        responseModel: successModel,
        stream,
        durationMs: Date.now() - attemptStart,
        usage: successUsage,
        cost: successCost,
        requestId,
        providerRequestId,
        callKind,
        requestExcerpt,
        responseExcerpt: shouldCapturePayloads
          ? (typeof clipText === 'function'
            ? clipText(stream ? (responseText || '') : (typeof extractResponseText === 'function' ? extractResponseText(response) : ''), maxExcerptChars)
            : null)
          : null
      });

      if (logAttemptsEnabled && requestLogReady && typeof insertRequestAttempt === 'function') {
        await insertRequestAttempt({
          requestHash: requestId,
          attemptHash: attemptKey,
          chatDocId,
          activeProjectId,
          billingOwnerUserId,
          initiatorUserId,
          ...successArtifacts.logInput
        });
      }

      requestAttemptsAudit.push(successArtifacts.auditEntry);

      if (!stream && !streamStageEmitted) {
        const usage = typeof extractUsage === 'function'
          ? extractUsage(response)
          : { promptTokens: 0, completionTokens: 0, totalTokens: 0 };
        const resolvedResponseModel = String(response?.model || candidateModel || '').trim() || null;
        const cost = typeof calculateUsageCost === 'function'
          ? calculateUsageCost({ modelId: resolvedResponseModel, usage })
          : { totalCostUsd: 0 };
        const streamResult = requestExecution?.streamResult || null;
        streamStageEmitted = true;
        if (typeof emitStage === 'function') {
          emitStage('stream', {
            status: streamResult?.status || 'ok',
            decisionCode: streamResult?.decisionCode || 'stream_single_response',
            stream: false,
            candidateModel: streamResult?.payload?.candidateModel || candidateModel || null,
            responseModel: resolvedResponseModel,
            usedLegacyRequest,
            transport: streamResult?.payload?.transport || 'single',
            usage,
            cost
          });
        }
      }

      if (stream && !streamStageEmitted) {
        const streamResult = requestExecution?.streamResult || null;
        streamStageEmitted = true;
        if (typeof emitStage === 'function') {
          emitStage('stream', {
            status: streamResult?.status || 'ok',
            decisionCode: streamResult?.decisionCode || 'stream_complete',
            stream: true,
            candidateModel: streamResult?.payload?.candidateModel || candidateModel || null,
            responseModel: String(response?.model || candidateModel || '').trim() || null,
            usedLegacyRequest,
            transport: streamResult?.payload?.transport || 'stream'
          });
        }
      }

      const normalizedResponseModel = typeof normalizeResponseModelId === 'function'
        ? normalizeResponseModelId(response?.model || null)
        : String(response?.model || '').trim() || null;
      if (typeof isBlockedResponseModel === 'function' && isBlockedResponseModel(normalizedResponseModel, blockedModelSet)) {
        const blockedError = new Error(`Blocked response model: ${normalizedResponseModel}`);
        blockedError.status = 409;
        blockedError.code = 'BLOCKED_RESPONSE_MODEL';
        blockedError.blockedModel = normalizedResponseModel;
        lastError = blockedError;
        if (typeof onTraceWarn === 'function') {
          onTraceWarn({
            requestId,
            stage: debugLabel,
            blockedModel: normalizedResponseModel,
            candidateModel: candidateModel || null,
            fallbackAttempt: index + 1
          });
        }
        if (index >= candidateQueue.length - 1) {
          throw blockedError;
        }
        response = null;
        responseText = '';
        continue;
      }

      break;
    } catch (error) {
      lastError = error;

      const failureArtifacts = createRequestAttemptFailureArtifacts({
        attemptIndex,
        attemptKey,
        usedLegacyRequest,
        candidateModel,
        responseModel: null,
        stream,
        durationMs: Date.now() - attemptStart,
        error,
        requestId,
        providerRequestId: String(error?.providerRequestId || '').trim() || null,
        callKind,
        requestExcerpt
      });

      if (logAttemptsEnabled && requestLogReady && typeof insertRequestAttempt === 'function') {
        try {
          await insertRequestAttempt({
            requestHash: requestId,
            attemptHash: attemptKey,
            chatDocId,
            activeProjectId,
            billingOwnerUserId,
            initiatorUserId,
            ...failureArtifacts.logInput
          });
        } catch (attemptError) {
          if (typeof onWarn === 'function') {
            onWarn(attemptError?.message || 'chat-request attempt log failed');
          }
        }
      }

      requestAttemptsAudit.push(failureArtifacts.auditEntry);

      if (typeof isRateLimitedStatus === 'function' && isRateLimitedStatus(error)) {
        if (typeof setRateLimitUntil === 'function') {
          setRateLimitUntil(Date.now() + Number(rateLimitCooldownMs || 0));
        }
        throw typeof buildRateLimitErrorFn === 'function'
          ? buildRateLimitErrorFn(rateLimitCooldownMs)
          : error;
      }

      const transientRetries = Number(transientRetriesByCandidate.get(retryKey) || 0);
      if (typeof isTransientRateLimitError === 'function'
        && isTransientRateLimitError(error)
        && transientRetries < Number(maxTransientRetriesPerCandidate || 0)) {
        const nextRetries = transientRetries + 1;
        transientRetriesByCandidate.set(retryKey, nextRetries);
        const delayMs = Number(baseTransientRetryDelayMs || 0) * nextRetries;
        if (typeof onTraceWarn === 'function') {
          onTraceWarn({
            requestId,
            stage: debugLabel,
            candidateModel: candidateModel || null,
            transientRetry: nextRetries,
            delayMs,
            reason: error?.message || 'transient provider/rate-limit error'
          });
        }
        if (typeof sleepFn === 'function') {
          await sleepFn(delayMs);
        }
        index -= 1;
        continue;
      }

      if (candidateModel && typeof isModelUnavailableError === 'function' && isModelUnavailableError(error)) {
        if (unavailableModelIds && typeof unavailableModelIds.add === 'function') {
          unavailableModelIds.add(candidateModel);
        }
        if (typeof onWarn === 'function') {
          onWarn(`marking model unavailable for session: ${candidateModel}`);
        }
      }

      if (candidateModel
        && typeof isGuardrailRestrictionError === 'function'
        && isGuardrailRestrictionError(error)
        && !workerDefaultQueued) {
        workerDefaultQueued = true;
        candidateQueue.splice(index + 1, candidateQueue.length - (index + 1), '');
        if (typeof onTraceWarn === 'function') {
          onTraceWarn({
            requestId,
            stage: debugLabel,
            guardrailRestricted: true,
            retryingWith: 'worker-default-model'
          });
        }
        continue;
      }

      const isLast = index >= candidateQueue.length - 1;
      if (!(typeof shouldRetryRequestError === 'function' && shouldRetryRequestError(error)) || isLast) {
        if (typeof isGuardrailRestrictionError === 'function' && isGuardrailRestrictionError(error)) {
          const policyError = new Error('OpenRouter request failed: No endpoints available matching your guardrail restrictions and data policy. Configure: https://openrouter.ai/settings/privacy');
          policyError.status = Number(error?.status || 0);
          policyError.code = 'OPENROUTER_POLICY_BLOCKED';
          policyError.cause = error;
          throw policyError;
        }
        throw error;
      }
    }
  }

  if (!response && !responseText) {
    const failure = lastError || new Error('Unable to generate assistant response.');
    failure.requestExecutionState = buildExecutionState();
    throw failure;
  }

  return buildExecutionState();
};

// Executes the primary request and falls back to legacy request messages when configured.
export const executeRequestWithLegacyFallback = async ({
  messages = [],
  executeRequest,
  runtimeEnabled = false,
  fallbackEnabled = null,
  legacyFallbackEnabled = false,
  requestId = null,
  buildFallbackMessages,
  buildLegacyMessages,
  docJson,
  userText = '',
  systemPrompt,
  maxHistoryMessages = 20,
  onTraceWarn
} = {}) => {
  const fallbackIsEnabled = fallbackEnabled === null
    ? legacyFallbackEnabled
    : Boolean(fallbackEnabled);
  const fallbackBuilder = typeof buildFallbackMessages === 'function'
    ? buildFallbackMessages
    : buildLegacyMessages;
  let response = null;
  let responseText = '';
  let lastError = null;
  let usedLegacyRequest = false;

  try {
    const requestResult = typeof executeRequest === 'function'
      ? await executeRequest(messages)
      : null;
    response = requestResult?.response || null;
    responseText = requestResult?.responseText || '';
  } catch (error) {
    lastError = error;
    if (String(error?.code || '') === 'OPENROUTER_RATE_LIMITED' || String(error?.code || '') === 'OPENROUTER_POLICY_BLOCKED') {
      throw error;
    }
    if (!(runtimeEnabled && fallbackIsEnabled)) {
      throw error;
    }

    usedLegacyRequest = true;
    if (typeof onTraceWarn === 'function') {
      onTraceWarn({
        requestId,
        stage: 'request',
        fallback: 'legacy-request',
        error: error?.message || 'orchestration request failed'
      });
    }

    const legacyMessages = typeof fallbackBuilder === 'function'
      ? fallbackBuilder({ docJson, userText, systemPrompt, maxHistoryMessages })
      : [];
    const fallbackResult = typeof executeRequest === 'function'
      ? await executeRequest(legacyMessages)
      : null;
    response = fallbackResult?.response || null;
    responseText = fallbackResult?.responseText || '';
  }

  if (!response && !responseText) {
    throw lastError || new Error('Unable to generate assistant response.');
  }

  return {
    response,
    responseText,
    usedLegacyRequest,
    usedFallbackRequest: usedLegacyRequest
  };
};

// Alias with behavior-oriented naming; legacy symbol remains for compatibility.
export const executeRequestWithFallbackMessages = executeRequestWithLegacyFallback;
